import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Le bandeau « À ce jour » de l'accueil: une rangée de chiffres dans l'ordre
 * du parcours de l'exploitant (le camion est libre, il roule, il est à
 * l'atelier, le carburant est à livrer, l'argent est à encaisser, les convois
 * avancent).
 *
 * Une ligne en grand écran, deux en moyen, deux colonnes sur téléphone.
 *
 * Chaque tuile reçoit:
 *  - `id`: identifiant de test (`data-testid`);
 *  - `icone`: icône lucide; `animation`: `respire`, `roule`, `oscille`,
 *    `goutte` ou `brille` — l'animation ne joue que si la valeur est non nulle;
 *  - `libelle` (court, à l'écran), `libelleComplet` (lu par les lecteurs
 *    d'écran et dans l'infobulle), `valeur` (nombre, ou `null` pendant le chargement),
 *    `formater` (nombre → texte affiché);
 *  - `sousLigne`, `vide` (texte quand la valeur vaut zéro);
 *  - `proportion` (0 à 1, facultative): mini-barre sous le chiffre;
 *  - `ton`: `disponible`, `route`, `atelier`, `critique`, `neutre`;
 *  - `onClick`: la navigation de la tuile.
 *
 * Les animations sont purement en CSS (classes `acj-*` de `index.css`), donc
 * aucune ne provoque de rendu React; elles s'arrêtent quand le système
 * demande moins de mouvement. Le seul rendu animé est le compteur, joué une
 * fois à l'apparition puis figé.
 */

const TONS = {
  disponible: {
    pastille: 'bg-success/10 text-success',
    barre: 'bg-success',
  },
  route: {
    pastille: 'bg-[hsl(var(--cat-route)/0.12)] text-[hsl(var(--cat-route))]',
    barre: 'bg-[hsl(var(--cat-route))]',
  },
  atelier: {
    pastille: 'bg-flame/10 text-flame-ink',
    barre: 'bg-flame',
  },
  critique: {
    pastille: 'bg-destructive/10 text-destructive',
    barre: 'bg-destructive',
  },
  neutre: {
    pastille: 'bg-primary/10 text-primary',
    barre: 'bg-primary',
  },
};

const TON_VIDE = 'bg-muted text-muted-foreground';

const COLONNES_GRAND_ECRAN = {
  1: 'xl:grid-cols-1',
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
  5: 'xl:grid-cols-5',
  6: 'xl:grid-cols-6',
};

const DUREE_COMPTEUR = 600;

/** Vrai quand le système demande de réduire les mouvements. */
export const mouvementReduit = () => {
  try {
    return Boolean(
      typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    );
  } catch {
    return false;
  }
};

/**
 * Compte de 0 à `cible` une seule fois, à la première valeur connue. Les
 * valeurs suivantes (rafraîchissement) s'affichent directement.
 */
export const useCompteur = (cible) => {
  const dejaJoue = useRef(false);
  const [courant, setCourant] = useState(() =>
    typeof cible === 'number' && mouvementReduit() ? cible : 0,
  );

  useEffect(() => {
    if (typeof cible !== 'number' || !Number.isFinite(cible)) return undefined;
    if (dejaJoue.current || cible === 0 || mouvementReduit() || typeof window.requestAnimationFrame !== 'function') {
      dejaJoue.current = true;
      setCourant(cible);
      return undefined;
    }
    dejaJoue.current = true;
    let image;
    let debut;
    const pas = (instant) => {
      if (debut === undefined) debut = instant;
      const t = Math.min((instant - debut) / DUREE_COMPTEUR, 1);
      // Décélération douce: le chiffre arrive, il ne s'écrase pas.
      const adouci = 1 - (1 - t) ** 3;
      setCourant(t >= 1 ? cible : cible * adouci);
      if (t < 1) image = window.requestAnimationFrame(pas);
    };
    image = window.requestAnimationFrame(pas);
    return () => {
      if (image) window.cancelAnimationFrame(image);
      setCourant(cible);
    };
  }, [cible]);

  return typeof cible === 'number' ? courant : null;
};

/** La taille du chiffre cède avant son contenu: jamais de points de suspension. */
const tailleValeur = (texte) => {
  const longueur = String(texte ?? '').length;
  if (longueur <= 6) return 'text-xl';
  if (longueur <= 9) return 'text-lg';
  return 'text-base';
};

const formatParDefaut = (n) => Math.round(n).toLocaleString('fr-FR');

const Pastille = ({ Icone, animation, actif, classeTon }) => (
  <span
    aria-hidden="true"
    className={cn(
      'acj-pastille relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors',
      classeTon,
      actif && animation === 'brille' && 'acj-brille',
    )}
  >
    {Icone ? (
      <Icone
        className={cn(
          'h-5 w-5',
          actif && animation === 'roule' && 'acj-roule',
          actif && animation === 'oscille' && 'acj-oscille',
          actif && animation === 'goutte' && 'acj-goutte',
        )}
      />
    ) : null}
    {actif && animation === 'roule' ? <span className="acj-route" /> : null}
    {actif && animation === 'respire' ? (
      <span className="acj-respire absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-card" />
    ) : null}
  </span>
);

const Tuile = ({ tuile }) => {
  const {
    id,
    icone,
    animation,
    libelle,
    libelleComplet,
    valeur,
    formater = formatParDefaut,
    sousLigne,
    vide,
    proportion,
    ton = 'neutre',
    onClick,
  } = tuile;
  const courant = useCompteur(typeof valeur === 'number' ? valeur : null);
  const enChargement = typeof valeur !== 'number';
  const estVide = !enChargement && valeur === 0;
  const texteFinal = enChargement ? '—' : formater(valeur);
  const texteAffiche = enChargement ? '—' : formater(courant ?? 0);
  const ligne = enChargement ? 'Chargement…' : estVide && vide ? vide : sousLigne;
  const tons = TONS[ton] || TONS.neutre;
  const ratio =
    typeof proportion === 'number' && Number.isFinite(proportion) && !enChargement
      ? Math.min(Math.max(proportion, 0), 1)
      : null;
  const etiquette = [
    `${libelleComplet || libelle} : ${texteFinal}`,
    ligne || null,
    onClick ? 'Voir le détail' : null,
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={etiquette}
      title={etiquette}
      data-testid={id}
      data-vide={estVide ? 'oui' : undefined}
      className={cn(
        'acj-tuile group relative flex h-full min-h-[64px] w-full items-center gap-2.5 overflow-hidden rounded-2xl border border-border/70 bg-card px-3 py-2 text-left shadow-sm',
        'transition-[box-shadow,transform,border-color] duration-200 ease-out',
        'hover:-translate-y-0.5 hover:border-border hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      )}
    >
      <Pastille
        Icone={icone}
        animation={animation}
        actif={!enChargement && !estVide}
        classeTon={estVide ? TON_VIDE : tons.pastille}
      />

      <span className="min-w-0 flex-1" aria-hidden="true">
        <span className="block truncate text-[11px] font-semibold leading-tight text-muted-foreground">
          {libelle}
        </span>
        <span className="flex h-6 min-w-0 items-center gap-2 overflow-hidden">
          <span
            className={cn(
              'kpi-value block whitespace-nowrap font-black leading-none tabular-nums',
              estVide || enChargement ? 'text-muted-foreground' : 'text-foreground',
              tailleValeur(texteFinal),
            )}
          >
            {texteAffiche}
          </span>
          {/* Mini-barre de proportion, à côté du chiffre pour ne pas grandir
              la tuile. */}
          {ratio !== null ? (
            <span className="block h-1 min-w-[24px] max-w-[56px] flex-1 overflow-hidden rounded-full bg-muted">
              <span
                className={cn('acj-barre block h-full rounded-full', tons.barre)}
                style={{ width: `${Math.round(ratio * 100)}%` }}
              />
            </span>
          ) : null}
        </span>
        {ligne ? (
          <span className="block truncate text-[11px] leading-tight text-muted-foreground">
            {ligne}
          </span>
        ) : null}
      </span>

      {onClick ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-2.5 top-2 hidden items-center gap-0.5 text-[10px] font-semibold text-muted-foreground opacity-0 transition-[opacity,transform] duration-200 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:opacity-100 sm:flex sm:-translate-x-1"
        >
          Voir
          <ArrowRight className="h-3 w-3" />
        </span>
      ) : null}
    </button>
  );
};

export const ACeJour = ({
  titre = 'À ce jour',
  titreId = 'a-ce-jour',
  tuiles = [],
  className,
  ...rest
}) => {
  const visibles = tuiles.filter(Boolean).slice(0, 6);
  return (
    <section aria-labelledby={titreId} className={cn('space-y-2', className)}>
      {/* <h2
        id={titreId}
        className="font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
      >
        {titre}
      </h2> */}
      <div
        role="list"
        className={cn(
          'grid grid-cols-2 gap-2.5 md:grid-cols-3',
          COLONNES_GRAND_ECRAN[visibles.length] || 'xl:grid-cols-6',
        )}
        {...rest}
      >
        {visibles.map((tuile) => (
          <div role="listitem" key={tuile.id} className="min-w-0 h-full">
            <Tuile tuile={tuile} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default ACeJour;
